import {
  call,
  put,
  all,
  spawn,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';

import moment from 'moment';

import {
  GENERATE_SETTLEMENTS_REPORTS_URL,
  SEARCH_SETTLEMENTS_VENDOR_URL,
  GENERATE_REPORT_URL,
  SEARCH_ORDERS_URL,
  GET_MERCHANT_PG_CONFIG_URL,
} from 'constants/url';
import request from '../../utils/request';
import { SEARCH_EASY_SPLIT_VENDOR_ALL_URL } from '../../utils/constants';
import { showToast } from '../Toast/actions';

import {
  saveReports,
  disableLoaderAction,
  handleGetReportByIdResponse,
  handleDownloadReportResponse,
  handleDeleteReportResponse,
  handleGenerateReportResponse,
  generateReportAction,
  enableLoaderAction,
} from './actions';
import {
  SEARCH_GENERATED_REPORTS,
  GET_REPORT_BY_ID,
  DOWNLOAD_REPORT,
  DELETE_REPORT,
  GENERATE_REPORT,
  GET_VENDOR_RECON_REPORT,
  SETTLEMENT_RECON_V2,
  SEARCH_VENDOR_RECON_SETTLEMENT_SPECIFIC_REPORT,
  SEARCH_GENERATED_REPORTS_URL,
  GET_REPORT_BY_ID_URL,
  DOWNLOAD_REPORT_URL,
  DELETE_REPORT_URL,
  GET_VENDOR_ORDER_RECON_REPORT,
  GET_MERCHANT_ROUTE_REQUEST,
  MERCHANT_ROUTES,
  FLOW_WISE_RECON,
  MERCHANT_RETARGET,
  SHARE_REPORT,
  POST_MERCHANT_SHARE_REPORT,
} from './constants';

export function* searchGeneratedReports({ data }) {
  try {
    const { activeNavigation, options: reqOptions, isModuleFederated } = data;
    const { url, method } = SEARCH_GENERATED_REPORTS_URL;
    const options = {
      headers: {
        'Content-type': 'application/json',
      },
      method,
      queryString: reqOptions || {},
      track: true,
      isModuleFederated,
    };
    const response = yield call(request, url, options);
    yield put(saveReports({ data: response, activeNavigation }));
  } catch (err) {
    yield put(disableLoaderAction());
    yield put(showToast(err));
  }
}

export function* getReportById({ data: { interval, reportId, count } }) {
  try {
    const { url, method } = GET_REPORT_BY_ID_URL;
    const updatedUrl = url.replace('$report_id$', reportId);
    const options = {
      headers: {
        'Content-type': 'application/json',
      },
      method,
    };
    const response = yield call(request, updatedUrl, options);
    if (interval) {
      const { status } = response;
      if (['DONE', 'ERROR'].includes(status)) clearInterval(interval);
      else if (count < 5 && status === 'REQUESTED')
        response.status = 'PROCESSING';
    }
    yield put(handleGetReportByIdResponse(response));
  } catch (err) {
    if (interval) clearInterval(interval);
    yield put(disableLoaderAction());
    yield put(showToast(err));
  }
}

export function* downloadReport({ data }) {
  try {
    const { reportId, type, cb } = data;
    const { url, method } = DOWNLOAD_REPORT_URL;
    const updatedUrl = `${url.replace('$report_id$', reportId)}${
      type ? `?reportType=${type}` : ''
    }`;
    const options = {
      headers: {
        'Content-type': 'application/json',
      },
      method,
    };
    const response = yield call(request, updatedUrl, options);
    if (cb) {
      cb({ response });
    } else {
      yield put(handleDownloadReportResponse(response));
    }
  } catch (err) {
    const { hideErrorAlert = false, cb } = data;
    yield put(disableLoaderAction());
    if (!hideErrorAlert) {
      yield put(showToast(err));
    }
    if (cb) {
      cb({ error: err });
    }
  }
}

export function* deleteReport({ data }) {
  try {
    const { reportId } = data;
    const { url, method } = DELETE_REPORT_URL;
    const updatedUrl = url.replace('$report_id$', reportId);
    const options = {
      headers: {
        'Content-type': 'application/json',
      },
      method,
    };
    const response = yield call(request, updatedUrl, options);
    yield put(handleDeleteReportResponse(response));
  } catch (err) {
    yield put(disableLoaderAction());
    yield put(showToast(err));
  }
}

export const getGenerateReportUrl = reportType => {
  if (
    [SETTLEMENT_RECON_V2, FLOW_WISE_RECON, 'offline_settlement_recon'].includes(
      reportType,
    )
  ) {
    return GENERATE_SETTLEMENTS_REPORTS_URL;
  }
  return GENERATE_REPORT_URL;
};

export function* generateReport({
  data: { options: reqOptions, cb, actions },
}) {
  try {
    const { url, method } = getGenerateReportUrl(reqOptions.reportType);
    const options = {
      headers: {
        'Content-type': 'application/json',
      },
      method,
      queryString: reqOptions || {},
      track: true,
    };
    const response = yield call(request, url, options);
    const result = { ...response };
    if (response && response.status === 'REQUESTED' && cb) {
      // eslint-disable-next-line no-underscore-dangle
      cb(response._id);
      result.status = 'PROCESSING';
    }
    yield put(handleGenerateReportResponse(result));
  } catch (err) {
    if (actions) actions.setSubmitting(false);
    yield put(disableLoaderAction());
    yield put(showToast(err));
  }
}

export function* getVendorReconReport({
  data: { options: reqOptions, cb, closeModal, actions },
}) {
  try {
    const { url, method } = SEARCH_EASY_SPLIT_VENDOR_ALL_URL;
    const options = {
      method,
      queryString: { merchantVendorId: reqOptions.vendorId },
    };
    const response = yield call(request, url, options);
    if (response.totalElements === 1) {
      closeModal();
      yield put(
        generateReportAction({
          cb,
          options: { ...reqOptions, vendorId: response.content[0].vendorId },
          actions,
        }),
      );
    } else {
      actions.setFieldError('vendorId', 'Invalid Vendor ID');
      actions.setSubmitting(false);
      yield put(disableLoaderAction());
    }
  } catch (err) {
    yield put(disableLoaderAction());
    closeModal();
    yield put(showToast(err));
  }
}

export function* getVendorOrderReconReport({
  data: { options: reqOptions, cb, closeModal, actions },
}) {
  try {
    const { url, method } = SEARCH_ORDERS_URL;
    const options = {
      method,
      queryString: { orderId: reqOptions.orderIds, pageNo: 0, pageSize: 10 },
    };
    const response = yield call(request, url, options);
    if (response.length > 0) {
      closeModal();
      yield put(
        generateReportAction({
          cb,
          options: { ...reqOptions },
          actions,
        }),
      );
    } else {
      actions.setFieldError('orderId', 'Invalid Order ID');
      actions.setSubmitting(false);
      yield put(disableLoaderAction());
    }
  } catch (err) {
    yield put(disableLoaderAction());
    closeModal();
    yield put(showToast(err));
  }
}

export function* getMerchantRoutes({ data: { cb } }) {
  try {
    const { url, method } = GET_MERCHANT_ROUTE_REQUEST;
    yield put(enableLoaderAction());
    const options = {
      headers: {
        'Content-type': 'application/json',
      },
      method,
    };
    const response = yield call(request, url, options);
    yield put(disableLoaderAction());
    cb(response);
  } catch (error) {
    yield put(disableLoaderAction());
  }
}

export function* getVendorReconSettlementSpecificReport({
  data: { options: reqOptions, cb, closeModal, actions },
}) {
  try {
    const { url, method } = SEARCH_SETTLEMENTS_VENDOR_URL;
    const options = {
      method,
      queryString: {
        pageNo: 0,
        pageSize: 10,
        payoutId: reqOptions.settlementId,
      },
    };
    const response = yield call(request, url, options);
    if (response && response.content && response.content.length > 0) {
      closeModal();
      yield put(
        generateReportAction({
          cb,
          options: {
            ...reqOptions,
            fileName: `${reqOptions.fileName} - ${reqOptions.settlementId}`,
            startDate: moment(
              response.content[0] ? response.content[0].periodStart : undefined,
            )
              .subtract(1, 'day')
              .startOf('day')
              .format('YYYY-MM-DD 00:00:00'),
            endDate: moment(
              response.content[0] ? response.content[0].addedOn : undefined,
            )
              .add(1, 'day')
              .endOf('day')
              .format('YYYY-MM-DD 23:59:59'),
          },
          actions,
        }),
      );
    } else {
      actions.setFieldError('settlementId', 'Invalid Settlement ID');
      actions.setSubmitting(false);
      yield put(disableLoaderAction());
    }
  } catch (err) {
    yield put(disableLoaderAction());
    closeModal();
    yield put(showToast(err));
  }
}

export function* getMerchantRetargetConfig({ data: { configKey, cb } }) {
  try {
    const { url, method } = GET_MERCHANT_PG_CONFIG_URL;
    const options = {
      method,
      queryString: { configKey },
    };
    const response = yield call(request, url, options);
    cb(response);
  } catch (err) {
    yield put(disableLoaderAction());
    yield put(showToast(err));
  }
}

export function* searchGeneratedReportsSaga() {
  yield takeEvery(SEARCH_GENERATED_REPORTS, searchGeneratedReports);
}

export function* getReportByIdSaga() {
  yield takeLatest(GET_REPORT_BY_ID, getReportById);
}

export function* reportDownloadSaga() {
  yield takeLatest(DOWNLOAD_REPORT, downloadReport);
}

export function* deleteReportSaga() {
  yield takeLatest(DELETE_REPORT, deleteReport);
}

export function* generateReportSaga() {
  yield takeLatest(GENERATE_REPORT, generateReport);
}

export function* getVendorReconReportSaga() {
  yield takeLatest(GET_VENDOR_RECON_REPORT, getVendorReconReport);
}

export function* getVendorOrderReconReportSaga() {
  yield takeLatest(GET_VENDOR_ORDER_RECON_REPORT, getVendorOrderReconReport);
}

export function* getMerchantRetargetConfigSaga() {
  yield takeLatest(MERCHANT_RETARGET, getMerchantRetargetConfig);
}

export function* getVendorReconSettlementSpecificReportSaga() {
  yield takeLatest(
    SEARCH_VENDOR_RECON_SETTLEMENT_SPECIFIC_REPORT,
    getVendorReconSettlementSpecificReport,
  );
}

export function* getMerchantRoutesSaga() {
  yield takeLatest(MERCHANT_ROUTES, getMerchantRoutes);
}

export function* getShareReport({ data: { reqOptions, successCb, errorCb } }) {
  try {
    const { url, method } = POST_MERCHANT_SHARE_REPORT;
    const options = {
      method,
      headers: {
        'Content-type': 'application/json',
      },
      body: JSON.stringify(reqOptions),
    };
    const response = yield call(request, url, options);
    successCb(response);
    yield put(
      showToast({
        type: 'SUCCESS',
        message: 'The report has been shared.',
      }),
    );
  } catch (err) {
    errorCb();
    yield put(
      showToast(
        new Error(`Unable to generate a shareable link. Please try again.`),
      ),
    );
  }
}

export function* getShareReportSaga() {
  yield takeLatest(SHARE_REPORT, getShareReport);
}

// Individual exports for testing
export default function* downloadReportSaga() {
  // See example in containers/HomePage/saga.js
  const sagas = [
    searchGeneratedReportsSaga,
    getReportByIdSaga,
    reportDownloadSaga,
    deleteReportSaga,
    generateReportSaga,
    getVendorReconReportSaga,
    getVendorOrderReconReportSaga,
    getVendorReconSettlementSpecificReportSaga,
    getMerchantRoutesSaga,
    getMerchantRetargetConfigSaga,
    getShareReportSaga,
  ];

  yield all(
    sagas.map(saga =>
      // eslint-disable-next-line func-names
      spawn(function*() {
        while (true) {
          try {
            yield call(saga);
            break;
          } catch (e) {
            // eslint-disable-next-line no-console
            console.log(e);
          }
        }
      }),
    ),
  );
}
